import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import practiceInstructorService from "../../services/practiceInstructorService";
import { useVisibility } from "../../contexts/VisibilityContext";
import LoadingSpinner from "../common/LoadingSpinner";
import AssignLearnerModal from "./AssignLearnerModal";

function PracticeInstructorStudents() {
  const [students, setStudents] = useState([]);
  const [pastStudents, setPastStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const navigate = useNavigate();
  const { canAccess } = useVisibility();
  const canViewGrades = canAccess("VIEW_ASSIGNED_LEARNER_GRADES");

  const getBadgeColor = (type) => {
    const t = (type || "").toString().toUpperCase();
    if (t.includes("APP") || t.includes("PLATTFORM") || t.includes("INFORMATIK")) {
      return "bg-blue-50 text-blue-700 border border-blue-100";
    }
    if (t.includes("MEDIAMATIK")) {
      return "bg-purple-50 text-purple-700 border border-purple-100";
    }
    if (t.includes("KAUF") || t.includes("KV")) {
      return "bg-gray-100 text-gray-700 border border-gray-200";
    }
    if (t.includes("DETAIL")) {
      return "bg-orange-50 text-orange-700 border border-orange-100";
    }
    if (t.includes("KUNDENDIALOG")) {
      return "bg-green-50 text-green-700 border border-green-100";
    }
    return "bg-gray-50 text-gray-600 border border-gray-200";
  };

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const [studentsData, pastStudentsData] = await Promise.all([
        practiceInstructorService.getMyStudents(),
        practiceInstructorService.getMyPastStudents(),
      ]);
      setStudents(studentsData);
      setPastStudents(pastStudentsData);
    } catch (err) {
      setError("Failed to load students.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentClick = (student) => {
    // Grades are the student detail view; respect the visibility setting.
    if (!canViewGrades) return;
    sessionStorage.setItem(`student-${student.id}`, JSON.stringify(student));
    navigate(`/students/${student.id}`);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <>
      <div className="min-h-screen bg-green-cloud py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
                    <i className="fas  fa-clipboard-check text-sunrise-red mr-4"></i>
                    Manage your Apprentices
                  </h1>
                  <p className="text-sm md:text-base text-slate-cloud">
                    Manage and monitor your assigned learners here. Click on a learner
                  </p>
                </div>
                <div className="mt-4 lg:mt-0">
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="inline-flex items-center px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors shadow-sm text-sm"
                  >
                    <i className="fas fa-user-plus mr-2"></i>
                    Search and assign learner
                  </button>
                </div>
              </div>
            </div>
          </div>

          {students.length === 0 ? (
            <div className="bg-white p-8 rounded-2xl shadow-sm text-center border border-gray-100">
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                <i className="fas fa-user-graduate"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-800">
                No Students Assigned
              </h3>
              <p className="text-gray-500 mt-2">
                You currently have no students assigned to you.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {students.map((student) => (
                <div
                  key={student.id}
                  onClick={() => handleStudentClick(student)}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 group cursor-pointer relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-red-50 rounded-bl-full -mr-12 -mt-12 transition-transform group-hover:scale-110"></div>

                  <div className="relative flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center text-white font-bold shadow-sm">
                      {student.firstName && student.firstName.charAt(0)}
                      {student.lastName && student.lastName.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 group-hover:text-red-600 transition-colors">
                        {student.firstName} {student.lastName}
                      </h3>
                      <p className="text-sm text-gray-500 mb-1">
                        {student.email}
                      </p>
                      {student.professionType && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getBadgeColor(student.professionType)}`}
                        >
                          {student.professionType}
                        </span>
                      )}
                    </div>
                  </div>

                  {canViewGrades && (
                    <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                        View Grades
                      </span>
                      <i className="fas fa-arrow-right text-gray-300 group-hover:text-red-500 transition-colors"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Past Students Section */}
          {pastStudents.length > 0 && (
            <div className="mt-12">
              <h2 className="text-xl font-bold text-grey-dusk flex items-center mb-6">
                <i className="fas fa-history text-gray-400 mr-3"></i>
                Past Apprentices
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pastStudents.map((student) => (
                  <div
                    key={student.id}
                    onClick={() => handleStudentClick(student)}
                    className="bg-white/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 group cursor-pointer relative overflow-hidden grayscale hover:grayscale-0"
                  >
                    <div className="relative flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center text-gray-500 font-bold">
                        {student.firstName && student.firstName.charAt(0)}
                        {student.lastName && student.lastName.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-700 group-hover:text-gray-900 transition-colors">
                          {student.firstName} {student.lastName}
                        </h3>
                        <p className="text-sm text-gray-500 mb-1">
                          {student.email}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Past Student
                        </span>
                      </div>
                    </div>

                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <AssignLearnerModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onAssigned={loadStudents}
      />
    </>
  );
}

export default PracticeInstructorStudents;
